import { OpportunitiesTable } from '@/components/OpportunitiesTable';
import { Button } from '@/components/ui/button';
import { RefreshCw, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';

const Opportunities = () => {
  const { toast } = useToast();
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState<{
    timestamp: string;
    count: number;
  } | null>(null);

  useEffect(() => {
    // Fetch last analysis timestamp
    const fetchLastAnalysis = async () => {
      const { data, error } = await supabase
        .from('token_opportunities')
        .select('collected_at')
        .order('collected_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        const { count } = await supabase
          .from('token_opportunities')
          .select('*', { count: 'exact', head: true })
          .eq('collection_date', new Date(data.collected_at).toISOString().split('T')[0]);

        setLastAnalysis({
          timestamp: data.collected_at,
          count: count || 0
        });
      }
    };

    fetchLastAnalysis();
  }, []);

  const handleRunAnalysis = async () => {
    setIsRunningAnalysis(true);
    
    toast({
      title: 'Starting Analysis',
      description: 'Running enhanced market analysis with AI scoring...',
    });

    try {
      const { data, error } = await supabase.functions.invoke('enhanced-market-analysis');

      if (error) throw error;

      const metadata = data?.metadata;
      const storageSuccess = metadata?.opportunities_stored > 0;

      toast({
        title: storageSuccess ? 'Analysis Complete' : 'Analysis Complete (Warning)',
        description: storageSuccess 
          ? `Analyzed ${metadata.total_analyzed} tokens, stored ${metadata.opportunities_stored} opportunities!`
          : 'Analysis complete but opportunities may not have been stored. Check logs.',
        variant: storageSuccess ? 'default' : 'destructive',
      });
      
      // Refresh the page after a short delay to show new data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: 'Analysis Failed',
        description: error instanceof Error ? error.message : 'Failed to run analysis',
        variant: 'destructive',
      });
    } finally {
      setIsRunningAnalysis(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-background/80 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Investment Opportunities
            </h1>
            <p className="text-muted-foreground mt-2">
              AI-powered analysis combining technicals, fundamentals, and sentiment
            </p>
            {lastAnalysis && (
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="gap-1">
                  <Clock className="h-3 w-3" />
                  Last analysis: {formatTimestamp(lastAnalysis.timestamp)}
                </Badge>
                <Badge variant="secondary">
                  {lastAnalysis.count} opportunities
                </Badge>
              </div>
            )}
          </div>
          
          <Button 
            onClick={handleRunAnalysis} 
            disabled={isRunningAnalysis}
            size="lg"
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRunningAnalysis ? 'animate-spin' : ''}`} />
            {isRunningAnalysis ? 'Analyzing...' : 'Run New Analysis'}
          </Button>
        </div>

        <OpportunitiesTable />
      </div>
    </div>
  );
};

export default Opportunities;
